import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Briefcase,
  FileBarChart,
  ClipboardCheck,
  ShieldCheck,
  LogOut,
  Plus,
  Building2,
  Users2,
  Target,
  Wrench,
  Folder,
  FileText,
  Receipt,
  ListChecks,
  ChevronLeft,
  ChevronRight,
  Award,
  Lock,
  Sliders,
  CreditCard,
} from "lucide-react";
import { canAccess } from "@/lib/plans";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AccountModal } from "@/pages/Admin";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", module: "tenders" },
  { to: "/tenders", label: "Tender List", icon: Briefcase, testid: "nav-tenders", module: "tenders" },
  { to: "/tender-report", label: "Tender Report", icon: FileBarChart, testid: "nav-tender-report", module: "tenders" },
  { to: "/plans", label: "Plans & Billing", icon: CreditCard, testid: "nav-plans" },
  { to: "/trca", label: "TRCA Assessment", icon: ClipboardCheck, testid: "nav-trca" },
];

// New section per trca_assessment_and_sidebar_spec — TRCA EPC organisation-level assessment.
const trcaEpcItems = [
  { to: "/trca-epc", label: "Take Assessment", icon: Award, testid: "nav-trca-epc" },
];

const moduleItems = [
  { to: "/docs/clients", label: "Clients", icon: Users2, testid: "nav-clients" },
  { to: "/docs/competitors", label: "Competitors", icon: Target, testid: "nav-competitors-docs" },
  { to: "/docs/vendors", label: "Vendors", icon: Wrench, testid: "nav-vendors" },
];

const formatItems = [
  { to: "/formats/general", label: "General", icon: Folder, testid: "nav-formats-general", module: "format_general" },
  { to: "/formats/technical", label: "Technical", icon: FileText, testid: "nav-formats-technical", module: "format_technical" },
  { to: "/formats/commercial", label: "Commercial", icon: Receipt, testid: "nav-formats-commercial", module: "format_commercial" },
];

const adminItems = [
  { to: "/admin", label: "Users & Roles", icon: ShieldCheck, testid: "nav-admin" },
  { to: "/admin/framework-locks", label: "Framework Locks", icon: Lock, testid: "nav-framework-locks" },
  { to: "/admin/framework-editor", label: "Framework Questions", icon: Sliders, testid: "nav-framework-editor" },
  { to: "/admin/trca", label: "TRCA Submissions", icon: ListChecks, testid: "nav-trca-submissions" },
  { to: "/admin/trca-epc", label: "TRCA EPC", icon: Award, testid: "nav-trca-epc-admin" },
  { to: "/admin/companies", label: "Companies", icon: Building2, testid: "nav-companies" },
];

const SIDEBAR_KEY = "twin.sidebar.collapsed";

function NavGroup({ items, sectionLabel, collapsed }) {
  const { user } = useAuth();
  return (
    <>
      {sectionLabel && !collapsed && (
        <div className="mt-4 mb-1 px-3 text-[10px] uppercase tracking-[0.18em] text-ink-muted">
          {sectionLabel}
        </div>
      )}
      {sectionLabel && collapsed && <div className="mt-3 mb-1 border-t border-ink-line/50" />}
      {items.map((item) => {
        const link = (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/" || item.to === "/admin"}
            data-testid={item.testid}
            className={({ isActive }) =>
              `flex items-center gap-2.5 ${collapsed ? "justify-center px-2" : "px-3"} py-2 text-[13px] rounded-sm transition-colors duration-150 ${
                isActive
                  ? "bg-brand text-white font-medium"
                  : "text-ink hover:bg-secondary"
              }`
            }
          >
            <item.icon className="w-4 h-4 shrink-0" />
            {!collapsed && <span className="truncate flex-1">{item.label}</span>}
            {!collapsed && item.module && !canAccess(user, item.module) && (
              <Lock className="w-3.5 h-3.5 opacity-60 shrink-0" data-testid={`${item.testid}-lock`} />
            )}
          </NavLink>
        );
        if (!collapsed) return link;
        return (
          <Tooltip key={item.to} delayDuration={150}>
            <TooltipTrigger asChild>{link}</TooltipTrigger>
            <TooltipContent side="right" className="font-display font-medium">{item.label}</TooltipContent>
          </Tooltip>
        );
      })}
    </>
  );
}

export default function Layout() {
  const { user, logout, setUser } = useAuth();
  const nav = useNavigate();
  const [showAccount, setShowAccount] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return window.localStorage.getItem(SIDEBAR_KEY) === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(SIDEBAR_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  // On narrow screens, default to collapsed if user hasn't set a preference.
  useEffect(() => {
    let stored = null;
    try { stored = window.localStorage.getItem(SIDEBAR_KEY); } catch { /* ignore */ }
    if (stored == null && typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches) {
      setCollapsed(true);
    }
  }, []);

  return (
    <TooltipProvider>
      <div className="min-h-screen flex bg-background">
        <aside
          data-testid="sidebar"
          data-collapsed={collapsed ? "true" : "false"}
          className={`${collapsed ? "w-16" : "w-64"} bg-white border-r border-ink-line flex flex-col sticky top-0 h-screen transition-[width] duration-300 ease-in-out`}
        >
          <div className={`${collapsed ? "px-2 justify-center" : "px-5"} py-6 border-b border-ink-line flex items-center`}>
            <div className="flex items-center gap-2 overflow-hidden">
              <div className="w-8 h-8 bg-brand grid place-items-center text-white font-display font-bold tracking-tight text-lg rounded-sm shrink-0" data-testid="brand-mark">
                T
              </div>
              {!collapsed && (
                <div className="min-w-0">
                  <div className="font-display font-bold text-[15px] leading-none tracking-tight">TWIN</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted mt-1 truncate">Tender Intelligence</div>
                </div>
              )}
            </div>
          </div>

          <nav className={`flex-1 py-4 ${collapsed ? "px-2" : "px-3"} space-y-0.5 nice-scroll overflow-y-auto`} aria-label="Primary">
            <NavGroup items={navItems} collapsed={collapsed} />
            <NavGroup items={trcaEpcItems} sectionLabel="TRCA EPC Assessment" collapsed={collapsed} />
            <NavGroup items={moduleItems} sectionLabel="Modules" collapsed={collapsed} />
            <NavGroup items={formatItems} sectionLabel="Formats" collapsed={collapsed} />
            {user?.role === "admin" && <NavGroup items={adminItems} sectionLabel="Admin" collapsed={collapsed} />}
          </nav>

          <div className={`${collapsed ? "p-2" : "p-3"} border-t border-ink-line space-y-2`}>
            {!canAccess(user, "tenders") ? null : collapsed ? (
              <Tooltip delayDuration={150}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => nav("/tenders/new")}
                    data-testid="new-tender-btn"
                    className="w-full flex items-center justify-center bg-brand hover:bg-brand-hover text-white text-[13px] font-medium px-2 py-2 rounded-sm transition-colors duration-150"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Add Tender</TooltipContent>
              </Tooltip>
            ) : (
              <button
                onClick={() => nav("/tenders/new")}
                data-testid="new-tender-btn"
                className="w-full flex items-center justify-center gap-1.5 bg-brand hover:bg-brand-hover text-white text-[13px] font-medium px-3 py-2 rounded-sm transition-colors duration-150"
              >
                <Plus className="w-4 h-4" /> Add Tender
              </button>
            )}

            <button
              type="button"
              onClick={() => setShowAccount(true)}
              data-testid="open-account-btn"
              title="My account & security"
              className={`flex items-center gap-2 ${collapsed ? "justify-center" : "px-2"} py-1.5 w-full rounded-sm hover:bg-secondary text-left transition-colors`}
            >
              <div className="w-8 h-8 bg-brand-soft text-brand grid place-items-center font-semibold text-xs rounded-sm shrink-0" data-testid="user-avatar">
                {(user?.name || user?.email || "U").slice(0, 2).toUpperCase()}
              </div>
              {!collapsed && (
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-medium truncate" data-testid="user-name">{user?.name || "User"}</div>
                    <div className="text-[10px] uppercase tracking-wider text-ink-muted truncate">
                      {user?.role?.replace("_", " ") || "member"}
                    </div>
                  </div>
              )}
            </button>

            {!collapsed && (
                  <button
                    onClick={logout}
                    data-testid="logout-btn"
                    className="w-full flex items-center gap-2 px-2 text-[12px] text-ink-muted hover:text-warn py-1.5 rounded-sm hover:bg-secondary transition-colors"
                    title="Sign out"
                  >
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
            )}

            {collapsed && (
              <Tooltip delayDuration={150}>
                <TooltipTrigger asChild>
                  <button onClick={logout} data-testid="logout-btn"
                    className="w-full flex items-center justify-center text-ink-muted hover:text-warn p-1.5 rounded-sm hover:bg-secondary transition-colors">
                    <LogOut className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Sign out</TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Collapse toggle — small chevron tab on the sidebar's right edge */}
          <button
            onClick={() => setCollapsed((s) => !s)}
            data-testid="sidebar-toggle"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="absolute -right-3 top-20 z-10 w-6 h-6 grid place-items-center bg-white border border-ink-line rounded-full shadow-sm hover:bg-secondary text-ink-muted hover:text-ink transition-colors"
          >
            {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
          </button>
        </aside>

        <main className="flex-1 min-w-0">
          <div className="max-w-[1600px] mx-auto px-8 py-8 animate-fade-up">
            <Outlet />
          </div>
        </main>
      </div>
      {showAccount && (
        <AccountModal me={user} onClose={() => setShowAccount(false)} onSaved={(u) => { setUser(u); setShowAccount(false); }} />
      )}
    </TooltipProvider>
  );
}
